/**
 * Compress an image file to a JPEG data URI.
 *
 * Numbers chosen to match `image_compressor.dart` on the Flutter side:
 *   * 2048px longest side — gives the CDN headroom to produce sharp
 *     1600px transforms on 3× DPR displays.
 *   * quality 0.92 — the sweet spot before the server's WebP q=95
 *     re-encode and the CDN's final pass. Anything lower and the
 *     combined generation loss becomes visible on high-DPR screens.
 *
 * The server (`main.py` → `process_image_field`) skips its own
 * re-encode when it receives a `data:image/jpeg` payload under 1.2 MB.
 * At 2048px q=0.92 the output lands in that range, so this function
 * becomes the only lossy pass on the upload path.
 */
export function compressImageToDataUri(file, {
  maxWidth = 2048,
  maxHeight = 2048,
  quality = 0.92,
} = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = width / height;
          if (ratio > 1) {
            height = Math.round(maxWidth / ratio);
            width = maxWidth;
          } else {
            width = Math.round(maxHeight * ratio);
            height = maxHeight;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        // Explicit settings so the canvas draw uses high-quality
        // interpolation rather than the default browser behavior.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);
        // Always encode as JPEG. Photos don't benefit from PNG and
        // the file size is 5–10× larger for no visual gain.
        const dataUri = canvas.toDataURL("image/jpeg", quality);
        resolve({ dataUri, width, height });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}